import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  Autocomplete,
  Chip,
  Alert,
  CircularProgress,
  Divider,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { supabase } from '../config/supabase';
import Layout from '../components/Layout';

export default function FamilyForm() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [availableContacts, setAvailableContacts] = useState([]);
  
  const [formData, setFormData] = useState({
    family_name: '',
    address: '',
    phone: '',
    email: '',
    anniversary_date: null,
    notes: '',
    members: [],
    primary_contact_id: null
  });

  useEffect(() => {
    fetchAvailableContacts();
    if (id) {
      fetchFamilyData();
    }
  }, [id]);

  const fetchAvailableContacts = async () => {
    try {
      const { data, error } = await supabase
        .from('contacts')
        .select('id, first_name, last_name, email');
      
      if (error) throw error;
      setAvailableContacts(data);
    } catch (error) {
      console.error('Error fetching contacts:', error);
      setError('Failed to load available contacts');
    }
  };

  const fetchFamilyData = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('families')
        .select(`
          *,
          members:family_members(
            contact_id,
            relationship
          )
        `)
        .eq('id', id)
        .single();

      if (error) throw error;

      if (data) {
        setFormData({
          ...data,
          anniversary_date: data.anniversary_date ? new Date(data.anniversary_date) : null,
          members: data.members.map(member => ({
            contact_id: member.contact_id,
            relationship: member.relationship
          }))
        });
      }
    } catch (error) {
      console.error('Error fetching family:', error);
      setError('Failed to load family data');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const familyData = {
        family_name: formData.family_name,
        address: formData.address,
        phone: formData.phone,
        email: formData.email,
        anniversary_date: formData.anniversary_date,
        notes: formData.notes,
        primary_contact_id: formData.primary_contact_id
      };

      let familyId = id;

      if (id) {
        // Update existing family
        const { error: updateError } = await supabase
          .from('families')
          .update(familyData)
          .eq('id', id);

        if (updateError) throw updateError;
      } else {
        // Create new family
        const { data, error: insertError } = await supabase
          .from('families')
          .insert(familyData)
          .select()
          .single();

        if (insertError) throw insertError;
        familyId = data.id;
      }

      // Handle family members
      if (familyId) {
        // Remove existing members
        await supabase
          .from('family_members')
          .delete()
          .eq('family_id', familyId);

        // Add new members
        if (formData.members.length > 0) {
          const memberData = formData.members.map(member => ({
            family_id: familyId,
            contact_id: member.contact_id,
            relationship: member.relationship
          }));

          const { error: membersError } = await supabase
            .from('family_members')
            .insert(memberData);

          if (membersError) throw membersError;
        }
      }

      setSuccess('Family saved successfully!');
      setTimeout(() => navigate('/families'), 1500);
    } catch (error) {
      console.error('Error saving family:', error);
      setError('Failed to save family data');
    } finally {
      setLoading(false);
    }
  };

  const handleAddMember = (contact) => {
    if (contact && !formData.members.find(m => m.contact_id === contact.id)) {
      setFormData(prev => ({
        ...prev,
        members: [...prev.members, {
          contact_id: contact.id,
          relationship: ''
        }]
      }));
    }
  };

  const handleRemoveMember = (contactId) => {
    setFormData(prev => ({
      ...prev,
      members: prev.members.filter(m => m.contact_id !== contactId)
    }));
  };

  const handleUpdateMemberRelationship = (contactId, relationship) => {
    setFormData(prev => ({
      ...prev,
      members: prev.members.map(m => 
        m.contact_id === contactId ? { ...m, relationship } : m
      )
    }));
  };

  return (
    <Layout>
      <Box sx={{ p: 3 }}>
        <Paper sx={{ p: 3, borderRadius: 2 }}>
          <Typography variant="h5" gutterBottom>
            {id ? 'Edit Family' : 'New Family'}
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {success}
            </Alert>
          )}

          <form onSubmit={handleSubmit}>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Family Name"
                  value={formData.family_name}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    family_name: e.target.value
                  }))}
                  required
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    email: e.target.value
                  }))}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Phone"
                  value={formData.phone}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    phone: e.target.value
                  }))}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DatePicker
                    label="Anniversary Date"
                    value={formData.anniversary_date}
                    onChange={(date) => setFormData(prev => ({
                      ...prev,
                      anniversary_date: date
                    }))}
                    renderInput={(params) => <TextField {...params} fullWidth />}
                  />
                </LocalizationProvider>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Address"
                  multiline
                  rows={2}
                  value={formData.address}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    address: e.target.value
                  }))}
                />
              </Grid>

              <Grid item xs={12}>
                <Divider sx={{ my: 2 }}>
                  <Typography variant="subtitle1">Family Members</Typography>
                </Divider>

                <Autocomplete
                  options={availableContacts}
                  getOptionLabel={(option) => `${option.first_name} ${option.last_name}`}
                  onChange={(_, value) => handleAddMember(value)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Add Family Member"
                      fullWidth
                    />
                  )}
                />

                <Box sx={{ mt: 2 }}>
                  {formData.members.map((member) => {
                    const contact = availableContacts.find(c => c.id === member.contact_id);
                    return contact ? (
                      <Box key={member.contact_id} sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Chip
                          label={`${contact.first_name} ${contact.last_name}`}
                          onDelete={() => handleRemoveMember(member.contact_id)}
                        />
                        <TextField
                          size="small"
                          label="Relationship"
                          value={member.relationship}
                          onChange={(e) => handleUpdateMemberRelationship(member.contact_id, e.target.value)}
                          sx={{ ml: 1 }}
                        />
                      </Box>
                    ) : null;
                  })}
                </Box>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Notes"
                  multiline
                  rows={3}
                  value={formData.notes}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    notes: e.target.value
                  }))}
                />
              </Grid>

              <Grid item xs={12}>
                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                  <Button
                    variant="outlined"
                    onClick={() => navigate('/families')}
                    disabled={loading}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    disabled={loading}
                  >
                    {loading ? <CircularProgress size={24} /> : (id ? 'Update' : 'Create')}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </form>
        </Paper>
      </Box>
    </Layout>
  );
}
